/** @jsxImportSource @emotion/react */
import React, { useMemo, useRef, useState } from "react";
import SelectedTokensButton from "../../ButterComponents/SelectedTokenButton/SelectedTokenButton";
import styled from "@emotion/styled";
import { handleGetChartData } from "utils/tokenSelectorUtils";
import { Chain, Hash } from "viem";
import { PlusButton } from "../../ButterComponents/PlusButton/PlusButton";
import { TokenSelectorPreview } from "../../TokenSelector/elements/TokenSelectorPreview";
import { TokenList } from "../../../store/global/getAllTokenLists";
import { motion } from "framer-motion";
import { fadeVariants } from "../../FilterBar/FilterBar";
import { useHover } from "usehooks-ts";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { TokenListMetadata } from "../../../hooks/queries/tokens-queries";

export interface TokenSectionProps {
  tokenAddress: Hash;
  showPlusButton: boolean;
  setIsWaitingToAddToken: React.Dispatch<React.SetStateAction<boolean>>;
  setIsChangingToken?: React.Dispatch<React.SetStateAction<Hash | undefined>>;
  isChangingToken?: boolean;
  hideBalancePercentages: () => void;
  isWaitingToAddToken?: boolean;
  tokenList: TokenList;
  chain: Chain;
  tokenListMetadata: TokenListMetadata | undefined;
}

export const TokenInputSection: React.FC<TokenSectionProps> = ({
  tokenAddress,
  showPlusButton,
  setIsWaitingToAddToken,
  setIsChangingToken,
  hideBalancePercentages,
  isWaitingToAddToken,
  isChangingToken,
  tokenList,
  chain,
  tokenListMetadata,
}) => {
  const [showTokenPreview, setShowTokenPreview] = useState<boolean>(false);
  const [tooltipTimeout, setTooltipTimeout] = useState<number | null>(null);
  const tokenPreviewRef = useRef<HTMLDivElement>(null);
  const isTokenPreviewHovered = useHover(tokenPreviewRef);
  const chartLists = GlobalStore.useState(s => s.chartLists);

  const TOOLTIP_DELAY = 400;
  const handleHover = () => {
    if (isWaitingToAddToken || isChangingToken) return;

    if (tooltipTimeout !== null) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }

    if (chartLists?.[tokenAddress] == null) {
      handleGetChartData(tokenAddress, chain, chartLists);
    }
    const newTimeoutId = window.setTimeout(() => {
      setShowTokenPreview(true);
    }, TOOLTIP_DELAY);

    setTooltipTimeout(newTimeoutId);
  };

  const handleMouseLeave = () => {
    if (tooltipTimeout !== null) {
      clearTimeout(tooltipTimeout);
    }
    if (!isTokenPreviewHovered) {
      console.log("leave", isTokenPreviewHovered);
      setShowTokenPreview(false);
    }
  };

  useMemo(() => {
    if (isWaitingToAddToken || isChangingToken) {
      setShowTokenPreview(false);
    }
  }, [isWaitingToAddToken, isChangingToken]);

  return (
    <Container
      onMouseLeave={() => {
        handleMouseLeave();
      }}
      data-testid={`token-input-section-${!tokenAddress ? "empty" : "nonEmpty"}-label`}
    >
      <div>
        <SelectedTokensButton
          onMouseEnter={handleHover}
          isVisible={true}
          tokenAddress={tokenAddress}
          onClick={() => {
            if (setIsChangingToken) {
              setIsChangingToken(tokenAddress);
            }
          }}
        />
        {showTokenPreview && (
          <TokenSelectorPreviewContainer
            onMouseLeave={() => {
              setShowTokenPreview(true);
            }}
            ref={tokenPreviewRef}
            variants={fadeVariants}
            initial={"hidden"}
            animate={"visible"}
          >
            <TokenSelectorPreview
              isVisible={showTokenPreview}
              tokenAddress={tokenAddress}
              tokenList={tokenList}
              isLoading={false}
              chain={chain}
              tokenListMetadata={tokenListMetadata}
            />
          </TokenSelectorPreviewContainer>
        )}
      </div>
      {showPlusButton && (
        <PlusButton
          isDisabled={isWaitingToAddToken ?? true}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            hideBalancePercentages();
            setIsWaitingToAddToken(true);
          }}
        />
      )}
    </Container>
  );
};

// Sub-components
const TokenSelectorPreviewContainer = styled(motion.div)`
  position: absolute;
  z-index: 99999999999;
  padding-top: 5px;
`;
const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
